import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Dispatch, bindActionCreators } from 'redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { RootState } from '../../reducers';
import * as actions from '../../actions/generalActions';
import * as agreementsActions from '../../actions/agreements';
import { withRouter } from 'react-router';
import { useUrlFilters } from '../../hooks/useUrlFilters';

import MikaPageHeader from '../../components/MikaPageHeader/MikaPageHeader';
import MikaExport, { ExportColumn } from '../../components/MikaExport/MikaExport';
import { axiosCaller } from '../../utils/apiCaller';
import MikaTable, { MikaColumn } from '../../components/MikaTable/MikaTable';
import MikaDateFilter, { DateRange } from '../../components/MikaDateFilter/MikaDateFilter';
import PaginationComponent from '../../components/Pagination/PaginationComponent';
import '../../components/Pagination/Pagination.scss';
import ContactDashboardStrip from '../Contact/ContactDashboardStrip';
import MikaViewToggle from '../../components/MikaViewToggle/MikaViewToggle';
import AgreementCardGrid from './AgreementCardGrid';
import '../Contact/Contact.scss';

const isMobile = window.innerWidth < 576;
const VIEW_STORAGE_KEY = 'agreementsViewMode';

interface Props {
    intl: any;
    actions: any;
    agreementsActions: any;
    history: any;
    location: any;
    loading: boolean;
    buttonLoader: boolean;
    permissions: any;
    statusId1Content: any[];
    statusId2Content: any[];
    statusId3Content: any[];
    statusId4Content: any[];
    statusId5Content: any[];
    clientsByModuleData: any[];
    statusId1Pagination: any;
    statusId2Pagination: any;
    statusId3Pagination: any;
    statusId4Pagination: any;
    statusId5Pagination: any;
    pagination: any;
}

const STATUS_VISUAL: Record<number, { color: string; bg: string }> = {
    1: { color: '#f59e0b', bg: '#fef3c7' },
    2: { color: '#3b82f6', bg: '#dbeafe' },
    3: { color: '#0891b2', bg: '#cffafe' },
    4: { color: '#22c55e', bg: '#dcfce7' },
    5: { color: '#ef4444', bg: '#fef2f2' },
};

const STATUS_I18N_KEY: Record<number, string> = {
    1: 'agreement.status.onHold',
    2: 'agreement.status.inProduction',
    3: 'agreement.status.inWarehouse',
    4: 'agreement.status.completed',
    5: 'agreement.status.canceled',
};

const StatusCell: React.FC<{ status: any }> = ({ status }) => {
    const id = status?.value;
    const visual = STATUS_VISUAL[id] || { color: '#94a3b8', bg: '#f1f5f9' };
    const key = STATUS_I18N_KEY[id];
    return (
        <span style={{ color: visual.color, background: visual.bg, padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 500, display: 'inline-block' }}>
            {key
                ? <FormattedMessage id={key} defaultMessage={status?.label || '\u2014'} />
                : (status?.label || '\u2014')}
        </span>
    );
};

type StripFilter = 'total' | 'status1' | 'status2' | 'status3' | 'status4' | 'status5' | 'cities' | 'agents';

const AgreementsListPage: React.FC<Props> = (props) => {
    const fm = (id: string, defaultMessage: string) => props.intl.formatMessage({ id, defaultMessage });

    const { filters, setFilter, setFilters, replaceFilters } = useUrlFilters(props.history, props.location, {
        page: { default: 1, type: 'number' },
        perPage: { default: 20, type: 'number' },
        status: { default: 'total', type: 'string' },
        search: { default: '', type: 'string' },
        dateFrom: { default: '', type: 'string' },
        dateTo: { default: '', type: 'string' },
        city: { default: '', type: 'string' },
        agentId: { default: '', type: 'string' },
        agentName: { default: '', type: 'string' },
    });

    const currentPage = filters.page;
    const perPage = filters.perPage;
    const searchTerm = filters.search;
    const stripFilter = filters.status as StripFilter;
    const dateRange: DateRange | null = (filters.dateFrom && filters.dateTo) ? { from: filters.dateFrom, to: filters.dateTo } : null;
    const activeCity: string | null = filters.city || null;
    const activeAgent: { id: number; name: string } | null = filters.agentId ? { id: Number(filters.agentId), name: filters.agentName } : null;

    const setCurrentPage = (p: number) => setFilter('page', p);
    const setPerPage = (p: number) => setFilters({ perPage: p, page: 1 });
    const setSearchTerm = (s: string) => replaceFilters({ search: s, page: 1 });
    const setDateRange = (d: DateRange | null) => setFilters({ dateFrom: d?.from || '', dateTo: d?.to || '', page: 1 });

    const [statsData, setStatsData] = useState<any>(null);
    const [viewMode, setViewMode] = useState<'table' | 'card'>(() => {
        return (localStorage.getItem(VIEW_STORAGE_KEY) as 'table' | 'card') || 'table';
    });

    const [cityPopoverOpen, setCityPopoverOpen] = useState(false);
    const [agentPopoverOpen, setAgentPopoverOpen] = useState(false);
    const [cityPopoverPos, setCityPopoverPos] = useState({ top: 0, right: 0 });
    const [agentPopoverPos, setAgentPopoverPos] = useState({ top: 0, right: 0 });
    const cityRef = useRef<HTMLDivElement>(null);
    const agentRef = useRef<HTMLDivElement>(null);
    const stripRef = useRef<HTMLDivElement>(null);

    const columns: MikaColumn[] = useMemo(() => [
        {
            key: 'id',
            title: '#',
            sortable: true,
            width: '50px',
            render: (val: any) => <span style={{ color: '#94a3b8', fontSize: '11px' }}>{val}</span>
        },
        {
            key: 'clientName',
            title: fm('agreement.list.column.client', 'Client'),
            sortable: true,
            render: (val: any, row: any) => `${val || ''} ${row.clientLastName || ''}`.trim() || '\u2014'
        },
        { key: 'project.label', title: fm('agreement.list.column.project', 'Project') },
        {
            key: 'agreementStatus',
            title: fm('agreement.list.column.status', 'Status'),
            render: (_: any, row: any) => <StatusCell status={row.agreementStatus} />,
        },
        { key: 'city', title: fm('agreement.list.column.city', 'City'), sortable: true, hideOnMobile: true },
        {
            key: 'total',
            title: fm('agreement.list.column.total', 'Total'),
            hideOnMobile: true,
            render: (val: any) => val ? `${Number(val).toFixed(2)}€` : '\u2014'
        },
        {
            key: 'productDates.latestEndDate',
            title: fm('agreement.list.column.deliveryDate', 'Delivery date'),
            sortable: true,
            hideOnMobile: true,
            render: (val: any) => val || '\u2014'
        },
        {
            key: 'user.userName',
            title: fm('agreement.list.column.agent', 'Agent'),
            hideOnMobile: true,
            render: (val: any) => val || '\u2014'
        }
    ], [props.intl]);

    const getStatusId = (strip: StripFilter): string | undefined => {
        if (strip === 'status1') return '1';
        if (strip === 'status2') return '2';
        if (strip === 'status3') return '3';
        if (strip === 'status4') return '4';
        if (strip === 'status5') return '5';
        return undefined;
    };

    const fetchData = () => {
        const statusId = getStatusId(stripFilter);
        const search = [activeCity, searchTerm].filter(Boolean).join(' ');
        const userId = activeAgent?.id ? String(activeAgent.id) : '';
        props.actions.getClientsForAgreement(
            statusId,
            dateRange?.from || undefined,
            dateRange?.to || undefined,
            currentPage,
            perPage,
            search,
            userId
        );
    };

    useEffect(() => {
        props.agreementsActions.getAgreementDashboardStats().then((res: any) => {
            if (res) setStatsData(res);
        });
    }, []);

    useEffect(() => {
        fetchData();
    }, [props.location.search]);

    const getCurrentData = () => {
        if (stripFilter === 'status1') return props.statusId1Content || [];
        if (stripFilter === 'status2') return props.statusId2Content || [];
        if (stripFilter === 'status3') return props.statusId3Content || [];
        if (stripFilter === 'status4') return props.statusId4Content || [];
        if (stripFilter === 'status5') return props.statusId5Content || [];
        return props.clientsByModuleData || [];
    };

    const getCurrentPagination = () => {
        if (stripFilter === 'status1') return props.statusId1Pagination;
        if (stripFilter === 'status2') return props.statusId2Pagination;
        if (stripFilter === 'status3') return props.statusId3Pagination;
        if (stripFilter === 'status4') return props.statusId4Pagination;
        if (stripFilter === 'status5') return props.statusId5Pagination;
        return props.pagination;
    };

    const data = getCurrentData();
    const pagination = getCurrentPagination();

    const dashboardStats = useMemo(() => {
        const s = statsData;
        return [
            { key: 'total', label: fm('agreement.dashboard.total', 'Total'), value: s?.total ?? pagination?.total ?? 0, icon: 'users' },
            { key: 'status1', label: fm('agreement.dashboard.onHold', 'On hold'), value: s?.status1 ?? '\u2014', icon: 'alert' },
            { key: 'status2', label: fm('agreement.dashboard.inProduction', 'In production'), value: s?.status2 ?? '\u2014', icon: 'folder' },
            { key: 'status3', label: fm('agreement.dashboard.inWarehouse', 'In warehouse'), value: s?.status3 ?? '\u2014', icon: 'userPlus' },
            { key: 'status4', label: fm('agreement.dashboard.completed', 'Completed'), value: s?.status4 ?? '\u2014', icon: 'check' },
            { key: 'status5', label: fm('agreement.dashboard.canceled', 'Canceled'), value: s?.status5 ?? '\u2014', icon: 'alert' },
            { key: 'cities', label: fm('agreement.dashboard.cities', 'Cities'), value: Object.keys(s?.cityDistribution || {}).length || '\u2014', icon: 'map' },
            { key: 'agents', label: fm('agreement.dashboard.agents', 'Agents'), value: (s?.agentDistribution || []).length || '\u2014', icon: 'agent' },
        ];
    }, [statsData, pagination, props.intl]);

    const cityDistribution = useMemo(() => {
        const dist = statsData?.cityDistribution;
        if (!dist) return [];
        return Object.entries(dist).sort((a: any, b: any) => b[1] - a[1]);
    }, [statsData]);

    const agentDistribution: { id: number; name: string; count: number }[] = useMemo(() => {
        return statsData?.agentDistribution || [];
    }, [statsData]);

    const positionPopoverUnderCard = (cardIndex: number) => {
        if (!stripRef.current) return { top: 0, right: 0 };
        const cards = stripRef.current.querySelectorAll('.cds-card');
        const card = cards[cardIndex];
        if (!card) return { top: 0, right: 0 };
        const rect = card.getBoundingClientRect();
        const popoverWidth = 220;
        const cardCenter = rect.left + rect.width / 2;
        return { top: rect.bottom + 4, right: window.innerWidth - cardCenter - popoverWidth / 2 };
    };

    const handleStripClick = (key: string) => {
        if (key === 'cities') {
            setAgentPopoverOpen(false);
            if (!cityPopoverOpen) setCityPopoverPos(positionPopoverUnderCard(5));
            setCityPopoverOpen(!cityPopoverOpen);
            return;
        }
        if (key === 'agents') {
            setCityPopoverOpen(false);
            if (!agentPopoverOpen) setAgentPopoverPos(positionPopoverUnderCard(6));
            setAgentPopoverOpen(!agentPopoverOpen);
            return;
        }
        setCityPopoverOpen(false);
        setAgentPopoverOpen(false);
        const newFilter = key as StripFilter;
        setFilters({
            status: newFilter === stripFilter ? 'total' : newFilter,
            page: 1,
            search: '',
            city: '',
            agentId: '',
            agentName: '',
        });
    };

    const handleCityFilter = (city: string) => { setFilters({ city: city, agentId: '', agentName: '', status: 'cities', page: 1 }); setCityPopoverOpen(false); };
    const handleAgentFilter = (agent: { id: number; name: string }) => { setFilters({ agentId: String(agent.id), agentName: agent.name, city: '', status: 'agents', page: 1 }); setAgentPopoverOpen(false); };
    const clearCityFilter = () => { setFilters({ city: '', status: 'total', page: 1 }); };
    const clearAgentFilter = () => { setFilters({ agentId: '', agentName: '', status: 'total', page: 1 }); };

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (cityRef.current && !cityRef.current.contains(e.target as Node)) setCityPopoverOpen(false);
            if (agentRef.current && !agentRef.current.contains(e.target as Node)) setAgentPopoverOpen(false);
        };
        if (cityPopoverOpen || agentPopoverOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [cityPopoverOpen, agentPopoverOpen]);

    const handleViewChange = (mode: 'table' | 'card') => {
        setViewMode(mode);
        localStorage.setItem(VIEW_STORAGE_KEY, mode);
    };

    const effectiveView = isMobile ? 'table' : viewMode;

    const editAgreement = (row: any) => {
        props.history.push({ pathname: `/dashboard/agreements/create/${row.id}/${row.project?.value}` });
    };

    const exportColumns: ExportColumn[] = [
        { key: 'id', title: '#' },
        { key: 'clientName', title: fm('agreement.list.column.client', 'Client'), render: (r) => `${r.clientName || ''} ${r.clientLastName || ''}`.trim() },
        { key: 'project.label', title: fm('agreement.list.column.project', 'Project') },
        {
            key: 'agreementStatus',
            title: fm('agreement.list.column.status', 'Status'),
            render: (r) => {
                const id = r?.agreementStatus?.value;
                const key = STATUS_I18N_KEY[id];
                return key ? fm(key, r?.agreementStatus?.label || '') : (r?.agreementStatus?.label || '');
            }
        },
        { key: 'city', title: fm('agreement.list.column.city', 'City') },
        { key: 'total', title: fm('agreement.list.column.total', 'Total') },
        { key: 'productDates.latestEndDate', title: fm('agreement.list.column.deliveryDate', 'Delivery date') },
        { key: 'user.userName', title: fm('agreement.list.column.agent', 'Agent') },
    ];

    const fetchAllForExport = async (): Promise<any[]> => {
        const statusId = getStatusId(stripFilter);
        const search = [activeCity, searchTerm].filter(Boolean).join(' ');
        const userId = activeAgent?.id ? String(activeAgent.id) : '';
        let qp: string[] = [];
        if (statusId) qp.push(`agreementStatusId=${statusId}`);
        if (dateRange?.from && dateRange?.to) { qp.push(`startDate=${dateRange.from}`); qp.push(`endDate=${dateRange.to}`); }
        qp.push('per_page=0');
        if (search) qp.push(`search=${encodeURIComponent(search)}`);
        if (userId) qp.push(`user_id=${userId}`);
        const res = await axiosCaller('GET', `agreement/filter?${qp.join('&')}`);
        return res?.data?.data || [];
    };

    const exportTitle = stripFilter === 'status1' ? fm('agreement.list.exportTitle.onHold', 'On hold agreements')
        : stripFilter === 'status2' ? fm('agreement.list.exportTitle.inProduction', 'In-production agreements')
        : stripFilter === 'status3' ? fm('agreement.list.exportTitle.inWarehouse', 'In-warehouse agreements')
        : stripFilter === 'status5' ? fm('agreement.list.exportTitle.canceled', 'Canceled agreements')
        : fm('agreement.list.exportTitle.default', 'Agreements');

    const tableActions = [
        {
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>,
            tooltip: fm('common.action.edit', 'Edit'),
            onClick: editAgreement
        }
    ];

    return (
        <div className="clp-page">
            {!isMobile && (
                <MikaPageHeader
                    title={fm('agreement.list.title', 'Agreements')}
                    subtitle={pagination ? `${pagination.total} total` : undefined}
                />
            )}

            <div className="clp-compact-toolbar">
                <div className="clp-search-wrap">
                    <svg className="clp-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
                    </svg>
                    <input type="text" className="clp-search-input" placeholder={fm('agreement.list.searchPlaceholder', 'Search agreements...')}
                        value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    {searchTerm && (
                        <button className="clp-search-clear" onClick={() => setSearchTerm('')} type="button">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                        </button>
                    )}
                </div>
                <div className="clp-toolbar-actions">
                    <MikaDateFilter value={dateRange} onChange={(r) => setDateRange(r)} />
                    {!isMobile && <MikaViewToggle mode={viewMode} onChange={handleViewChange} />}
                    <MikaExport fetchAllData={fetchAllForExport} columns={exportColumns} fileName={fm('agreement.list.exportFilename', 'agreements')} title={exportTitle} companyProfile={(props as any).companyProfile} />
                </div>
            </div>

            <div ref={stripRef}>
                        <ContactDashboardStrip stats={dashboardStats} activeFilter={stripFilter} onFilterClick={handleStripClick} />
                    </div>
                    {cityPopoverOpen && (
                        <div className="clp-city-popover" ref={cityRef} style={{ top: cityPopoverPos.top, right: cityPopoverPos.right }}>
                            <div className="clp-city-header"><FormattedMessage id="agreement.dashboard.topCities" defaultMessage="Top cities" /></div>
                            {cityDistribution.length === 0 ? <div className="clp-city-empty"><FormattedMessage id="common.noData" defaultMessage="No data available" /></div> :
                                cityDistribution.map(([city, count]) => (
                                    <button key={city} className={`clp-city-row ${activeCity === city ? 'clp-city-row-active' : ''}`} onClick={() => handleCityFilter(city)} type="button">
                                        <span className="clp-city-name">{city}</span><span className="clp-city-count">{count}</span>
                                    </button>
                                ))
                            }
                        </div>
                    )}
                    {agentPopoverOpen && (
                        <div className="clp-city-popover" ref={agentRef} style={{ top: agentPopoverPos.top, right: agentPopoverPos.right }}>
                            <div className="clp-city-header"><FormattedMessage id="agreement.dashboard.agents" defaultMessage="Agents" /></div>
                            {agentDistribution.length === 0 ? <div className="clp-city-empty"><FormattedMessage id="common.noData" defaultMessage="No data available" /></div> :
                                agentDistribution.map((agent) => (
                                    <button key={agent.id} className={`clp-city-row ${activeAgent?.id === agent.id ? 'clp-city-row-active' : ''}`} onClick={() => handleAgentFilter(agent)} type="button">
                                        <span className="clp-city-name">{agent.name}</span><span className="clp-city-count">{agent.count}</span>
                                    </button>
                                ))
                            }
                        </div>
                    )}

            {(activeCity || activeAgent) && (
                <div className="clp-active-filter-bar">
                    {activeCity && (
                        <div className="clp-active-filter-chip">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
                            <span>{activeCity}</span>
                            <span className="clp-active-filter-count">{pagination?.total ?? 0} <FormattedMessage id="agreement.list.records" defaultMessage="agreements" /></span>
                            <button className="clp-active-filter-clear" onClick={clearCityFilter} type="button"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12" /></svg></button>
                        </div>
                    )}
                    {activeAgent && (
                        <div className="clp-active-filter-chip">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                            <span>{activeAgent.name}</span>
                            <span className="clp-active-filter-count">{pagination?.total ?? 0} <FormattedMessage id="agreement.list.records" defaultMessage="agreements" /></span>
                            <button className="clp-active-filter-clear" onClick={clearAgentFilter} type="button"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12" /></svg></button>
                        </div>
                    )}
                </div>
            )}

            {effectiveView === 'table'
                ? <MikaTable columns={columns} data={data} actions={tableActions} loading={props.loading} onRowClick={editAgreement} />
                : <AgreementCardGrid data={data} onEdit={editAgreement} />
            }

            <PaginationComponent
                currentPage={currentPage} perPage={perPage} pagination={pagination}
                onPageChange={(p) => setCurrentPage(p)} onPerPageChange={(v) => setPerPage(v)}
                label={fm('agreement.list.records', 'agreements')}
            />
        </div>
    );
};

function mapStateToProps(state: RootState, ownProps: any) {
    return { ...ownProps, ...state.app, agreements: state.agreements };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        actions: bindActionCreators(actions as any, dispatch),
        agreementsActions: bindActionCreators(agreementsActions as any, dispatch),
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(AgreementsListPage as any)));
